export interface RelatedBreed {
  name: string;
  slug: string;
}

export interface Review {
  author: string;
  body: string;
}

export interface CreateBreedSchemaArgs {
  breed: string;
  breedPlural: string;
  slug: string;
  shortDescription: string;
  wikipediaUrl: string;
  healthIssue1: string;
  healthIssue2: string;
  reviews: Review[];
  relatedBreeds: RelatedBreed[];
  baseUrl: string;
}

export function createBreedSchema({
  breed,
  breedPlural,
  slug,
  shortDescription,
  wikipediaUrl,
  healthIssue1,
  healthIssue2,
  reviews,
  relatedBreeds,
  baseUrl,
}: CreateBreedSchemaArgs) {
  const breedUrl = `${baseUrl}/breed/${slug}`;
  const healthIssuesPhrase =
    healthIssue1 || healthIssue2
      ? `, including ${[healthIssue1, healthIssue2].filter(Boolean).join(' and ')}`
      : '';

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${breedUrl}#webpage`,
      name: `Fresh Dog Food for ${breedPlural} | The Farmer's Dog`,
      url: breedUrl,
      description: `The best dog food for ${breedPlural}. Human-grade, vet-developed recipes pre-portioned for your ${breed}'s age, weight, and health needs.`,
      publisher: {
        '@id': `${baseUrl}/#organization`,
      },
      about: {
        '@type': 'Animal',
        name: breed,
        ...(shortDescription && { description: shortDescription }),
        ...(wikipediaUrl && { additionalType: wikipediaUrl }),
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Breeds',
            item: `${baseUrl}/breed`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: breed,
            item: breedUrl,
          },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `Fresh Dog Food for ${breedPlural}`,
      description: `Personalized, human-grade fresh dog food plans designed for ${breedPlural}. Vet-developed recipes pre-portioned for your dog's age, weight, and activity level.`,
      brand: {
        '@type': 'Brand',
        name: "The Farmer's Dog",
      },
      url: breedUrl,
      offers: {
        '@type': 'Offer',
        url: `${baseUrl}/signup`,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: {
          '@id': `${baseUrl}/#organization`,
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: String(reviews.length),
        bestRating: '5',
      },
      review: reviews.map(({ author, body }) => ({
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: author,
        },
        reviewBody: body,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `What's the best dog food for ${breedPlural}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `The best dog food for ${breedPlural} is fresh, human-grade food tailored to the breed's specific health needs. A fresh diet contributes to a healthy, active life and can help stave off ailments common to ${breedPlural}${healthIssuesPhrase}. The Farmer's Dog offers fresh recipes pre-portioned to your dog's age, weight, and activity level. Learn more at ${baseUrl}/faq`,
          },
        },
        {
          '@type': 'Question',
          name: `Best dog food for ${breedPlural}`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `The best dog food for ${breedPlural} is fresh, human-grade food tailored to the breed's specific health needs. A fresh diet contributes to a healthy, active life and can help stave off ailments common to ${breedPlural}${healthIssuesPhrase}. The Farmer's Dog offers fresh recipes pre-portioned to your dog's age, weight, and activity level. Learn more at ${baseUrl}/faq`,
          },
        },
        {
          '@type': 'Question',
          name: `Are ${breedPlural} prone to joint problems?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes, ${breedPlural} ${healthIssue1 ? `are prone to ${healthIssue1}` : 'can be prone to joint problems'}. Maintaining a lean body condition helps, and fresh food containing Omega-3 fatty acids has been proven to reduce inflammation and improve arthritis symptoms.`,
          },
        },
        {
          '@type': 'Question',
          name: `Can fresh food help ${breedPlural} with allergies or sensitive skin?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes. For ${breedPlural} with dry and itchy skin, sensitive stomachs, or signs of food allergies, fresh food makes it easy to know exactly what's in the bowl — unlike kibble, which may contain unlisted ingredients.`,
          },
        },
        {
          '@type': 'Question',
          name: `How does fresh food help ${breedPlural} maintain a healthy weight?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Being even slightly overweight increases a ${breed}'s risk of arthritis, diabetes, and cancer. A fresh-food plan tailored to your dog's needs and delivered in pre-portioned packs makes weight maintenance easy and precise.`,
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Related Dog Breeds',
      itemListElement: relatedBreeds.map(
        ({ name, slug: breedSlug }, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name,
          url: `${baseUrl}/breed/${breedSlug}`,
        })
      ),
    },
  ];
}
